import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  CircularProgress,
  Typography,
  Paper,
  IconButton,
  SelectChangeEvent,
  Divider,
  Alert
} from '@mui/material';
import { Close as CloseIcon, Send as SendIcon } from '@mui/icons-material';
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { useAuth } from '../../context/auth-provider';

interface ApiTesterProps {
  open: boolean;
  onClose: () => void;
  baseUrl: string;
  title?: string;
}

export default function ApiTester({ open, onClose, baseUrl, title = 'API Tester' }: ApiTesterProps) {
  // Get auth context for user token
  const { user } = useAuth();
  
  // State for form and responses
  const [resourcePath, setResourcePath] = useState('');
  const [method, setMethod] = useState('GET');
  const [requestBody, setRequestBody] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState<{
    status?: number;
    statusText?: string;
    data?: any;
    error?: any;
  }>({});
  const [authError, setAuthError] = useState<string | null>(null);
  
  // Create refs for axios and abort controller
  const axiosInstance = useRef<AxiosInstance | null>(null);
  const abortController = useRef<AbortController>(new AbortController());

  // Initialize axios instance with auth token
  useEffect(() => {
    // Clean up previous requests on unmount
    return () => {
      if (abortController.current) {
        abortController.current.abort();
      }
    };
  }, []);

  // Initialize axios when component mounts or user changes
  useEffect(() => {
    if (!user) {
      setAuthError("You must be logged in to use the API tester");
      return;
    }
    
    setAuthError(null);
    initAxiosInstance();
  }, [user, open]);

  // Initialize axios instance with auth headers
  const initAxiosInstance = () => {
    if (!user || axiosInstance.current) return;
    
    axiosInstance.current = axios.create({
      baseURL: baseUrl,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': user.idToken
      },
      withCredentials: true 
    });
  };

  const handleMethodChange = (event: SelectChangeEvent<string>) => {
    setMethod(event.target.value);
  };

  const handleSubmit = async () => {
    if (!user || !axiosInstance.current) {
      setAuthError("Authentication required. Please log in.");
      return;
    }

    setIsLoading(true);
    setResponse({});
    setAuthError(null);

    // Create a new abort controller for this request
    abortController.current = new AbortController();

    try {
      // Prepare request config
      const config: AxiosRequestConfig = {
        method: method as any,
        url: resourcePath,
        signal: abortController.current.signal
      };
      
      // Add request body for appropriate methods
      if (['POST', 'PUT', 'PATCH'].includes(method) && requestBody.trim()) {
        try {
          config.data = JSON.parse(requestBody);
        } catch (e) {
          setResponse({
            status: 400,
            statusText: 'Bad Request',
            error: 'Invalid JSON in request body'
          });
          setIsLoading(false);
          return;
        }
      }

      // Make the API call
      const result = await axiosInstance.current(config);
      
      setResponse({
        status: result.status,
        statusText: result.statusText,
        data: result.data
      });
    } catch (error: any) {
      console.error('API call error:', error);
      
      // Handle various error types
      if (error.name === 'AbortError') {
        setResponse({
          status: 0,
          statusText: 'Request Aborted',
          error: 'The request was aborted'
        });
      } else if (error.response) {
        // Server responded with an error status
        setResponse({
          status: error.response.status,
          statusText: error.response.statusText || error.message,
          error: error.response.data || error.message,
          data: error.response.data
        });
        
        // If we get a 401 or 403, we might need to refresh the token
        if (error.response.status === 401 || error.response.status === 403) {
          setAuthError("Authentication error. Your session may have expired.");
        }
      } else if (error.request) {
        // Request was made but no response received
        setResponse({
          status: 0,
          statusText: 'No Response',
          error: 'The server did not respond'
        });
      } else {
        // Something else went wrong
        setResponse({
          status: 0,
          statusText: 'Error',
          error: error.message
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const prettyPrintResponse = () => {
    if (!response.data && !response.error) return null;
    
    try {
      const data = response.data || response.error;
      return JSON.stringify(data, null, 2);
    } catch (e) {
      return String(response.data || response.error);
    }
  };

  const getStatusColor = () => {
    const status = response.status;
    if (!status) return 'text.secondary';
    if (status >= 200 && status < 300) return 'success.main';
    if (status >= 400) return 'error.main';
    return 'warning.main';
  };

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      // Reset on close
      setResponse({});
      axiosInstance.current = null;
    } else {
      // Initialize on open
      initAxiosInstance();
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '80vh', display: 'flex', flexDirection: 'column' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {title}
        <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
        {/* Auth error alert */}
        {authError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {authError}
          </Alert>
        )}
        
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          mb: 3,
          mt: 1,
          alignItems: 'flex-start',
        }}>
          <FormControl sx={{ 
            width: '120px'
          }}>
            <InputLabel id="http-method-label">Method</InputLabel>
            <Select
              labelId="http-method-label"
              value={method}
              label="Method"
              onChange={handleMethodChange}
              sx={{ height: '56px' }}
            >
              <MenuItem value="GET">GET</MenuItem>
              <MenuItem value="POST">POST</MenuItem>
              <MenuItem value="PUT">PUT</MenuItem>
              <MenuItem value="PATCH">PATCH</MenuItem>
              <MenuItem value="DELETE">DELETE</MenuItem>
            </Select>
          </FormControl>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
            <TextField
              label="Resource Path"
              value={resourcePath}
              onChange={(e) => setResourcePath(e.target.value)}
              placeholder="/endpoint/resource"
              fullWidth
              size="medium"
            />
            <Typography variant="caption" sx={{ mt: 0.5, color: 'text.secondary' }}>
              Base URL: {baseUrl}
            </Typography>
          </Box>
          
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isLoading || !resourcePath.trim() || !user}
            startIcon={isLoading ? <CircularProgress size={20} /> : <SendIcon />}
            sx={{ 
              height: '56px',
              minWidth: '120px'
            }}
          >
            {isLoading ? 'Sending...' : 'Send'}
          </Button>
        </Box>
        
        {['POST', 'PUT', 'PATCH'].includes(method) && (
          <Box sx={{ mb: 2 }}>
            <TextField
              label="Request Body (JSON)"
              value={requestBody}
              onChange={(e) => setRequestBody(e.target.value)}
              multiline
              rows={4}
              fullWidth
              sx={{ fontFamily: 'monospace' }}
              error={requestBody.trim() !== '' && !isValidJson(requestBody)}
              helperText={requestBody.trim() !== '' && !isValidJson(requestBody) ? 'Invalid JSON format' : ''}
            />
          </Box>
        )}
        
        <Divider sx={{ my: 2 }} />
        
        <Typography variant="subtitle2" gutterBottom>
          Response
          {response.status && (
            <Box component="span" sx={{ ml: 2, color: getStatusColor() }}>
              {response.status} {response.statusText}
            </Box>
          )}
        </Typography>
        
        <Paper
          variant="outlined"
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            p: 2,
            bgcolor: '#f5f5f5',
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            whiteSpace: 'pre-wrap',
            position: 'relative'
          }}
        >
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : (
            prettyPrintResponse()
          )}
        </Paper>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// Helper function to validate JSON
function isValidJson(str: string): boolean {
  if (!str.trim()) return true;
  try {
    JSON.parse(str);
    return true;
  } catch (e) {
    return false;
  }
}


// import React, { useState } from 'react';
// import {
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   Button,
//   TextField,
//   Select,
//   MenuItem,
//   FormControl,
//   InputLabel,
//   Box,
//   CircularProgress,
//   Typography,
//   Paper,
//   IconButton,
//   SelectChangeEvent,
//   Divider
// } from '@mui/material';
// import { Close as CloseIcon, Send as SendIcon } from '@mui/icons-material';
// import axios from 'axios';

// interface ApiTesterProps {
//   open: boolean;
//   onClose: () => void;
//   baseUrl: string;
//   title?: string;
// }

// export default function ApiTester({ open, onClose, baseUrl, title = 'API Tester' }: ApiTesterProps) {
  
//   const [resourcePath, setResourcePath] = useState('');
//   const [method, setMethod] = useState('GET');
//   const [requestBody, setRequestBody] = useState('');
//   const [isLoading, setIsLoading] = useState(false);
//   const [response, setResponse] = useState<{
//     status?: number;
//     statusText?: string;
//     data?: any;
//     error?: any;
//   }>({});

//   const handleMethodChange = (event: SelectChangeEvent<string>) => {
//     setMethod(event.target.value);
//   };

//   const handleSubmit = async () => {
//     setIsLoading(true);
//     setResponse({});

//     try {

//       const url = `${baseUrl}${resourcePath}`;
      
//       const config = {
//         method,
//         url,
//         headers: {
//           'Content-Type': 'application/json',
//         },
//         data: ['POST', 'PUT', 'PATCH'].includes(method) && requestBody ? 
//           JSON.parse(requestBody) : undefined
//       };

//       const result = await axios(config);
      
//       setResponse({
//         status: result.status,
//         statusText: result.statusText,
//         data: result.data
//       });
//     } catch (error: any) {
//       setResponse({
//         status: error.response?.status,
//         statusText: error.response?.statusText || error.message,
//         error: error.response?.data || error.message,
//         data: error.response?.data
//       });
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const prettyPrintResponse = () => {
//     if (!response.data && !response.error) return null;
    
//     try {
//       const data = response.data || response.error;
//       return JSON.stringify(data, null, 2);
//     } catch (e) {
//       return String(response.data || response.error);
//     }
//   };

//   const getStatusColor = () => {
//     const status = response.status;
//     if (!status) return 'text.secondary';
//     if (status >= 200 && status < 300) return 'success.main';
//     if (status >= 400) return 'error.main';
//     return 'warning.main';
//   };

//   return (
//     <Dialog
//       open={open}
//       onClose={onClose}
//       maxWidth="md"
//       fullWidth
//       PaperProps={{
//         sx: { height: '80vh', display: 'flex', flexDirection: 'column' }
//       }}
//     >
//       <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
//         {title}
//         <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
//           <CloseIcon />
//         </IconButton>
//       </DialogTitle>
      
//       <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
//         <Box sx={{ 
//           display: 'flex', 
//           gap: 2, 
//           mb: 3,
//           mt: 1,
//           alignItems: 'flex-start',
//         }}>
//           <FormControl sx={{ 
//             width: '120px'
//           }}>
//             <InputLabel id="http-method-label">Method</InputLabel>
//             <Select
//               labelId="http-method-label"
//               value={method}
//               label="Method"
//               onChange={handleMethodChange}
//               sx={{ height: '56px' }}
//             >
//               <MenuItem value="GET">GET</MenuItem>
//               <MenuItem value="POST">POST</MenuItem>
//               <MenuItem value="PUT">PUT</MenuItem>
//               <MenuItem value="PATCH">PATCH</MenuItem>
//               <MenuItem value="DELETE">DELETE</MenuItem>
//             </Select>
//           </FormControl>
          
//           <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
//             <TextField
//               label="Resource Path"
//               value={resourcePath}
//               onChange={(e) => setResourcePath(e.target.value)}
//               placeholder="/endpoint/resource"
//               fullWidth
//               size="medium"
//               // Remove the helper text from here
//             />
//             <Typography variant="caption" sx={{ mt: 0.5, color: 'text.secondary' }}>
//               Base URL: {baseUrl}
//             </Typography>
//           </Box>
          
//           <Button
//             variant="contained"
//             color="primary"
//             onClick={handleSubmit}
//             disabled={isLoading || !resourcePath.trim()}
//             startIcon={isLoading ? <CircularProgress size={20} /> : <SendIcon />}
//             sx={{ 
//               height: '56px',  // Match height of other controls
//               minWidth: '120px'  // Ensure consistent width
//             }}
//           >
//             {isLoading ? 'Sending...' : 'Send'}
//           </Button>
//         </Box>
        
//         {['POST', 'PUT', 'PATCH'].includes(method) && (
//           <Box sx={{ mb: 2 }}>
//             <TextField
//               label="Request Body (JSON)"
//               value={requestBody}
//               onChange={(e) => setRequestBody(e.target.value)}
//               multiline
//               rows={4}
//               fullWidth
//               sx={{ fontFamily: 'monospace' }}
//             />
//           </Box>
//         )}
        
//         <Divider sx={{ my: 2 }} />
        
//         <Typography variant="subtitle2" gutterBottom>
//           Response
//           {response.status && (
//             <Box component="span" sx={{ ml: 2, color: getStatusColor() }}>
//               {response.status} {response.statusText}
//             </Box>
//           )}
//         </Typography>
        
//         <Paper
//           variant="outlined"
//           sx={{
//             flexGrow: 1,
//             overflow: 'auto',
//             p: 2,
//             bgcolor: '#f5f5f5',
//             fontFamily: 'monospace',
//             fontSize: '0.875rem',
//             whiteSpace: 'pre-wrap',
//             position: 'relative'
//           }}
//         >
//           {isLoading ? (
//             <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
//               <CircularProgress />
//             </Box>
//           ) : (
//             prettyPrintResponse()
//           )}
//         </Paper>
//       </DialogContent>
      
//       <DialogActions>
//         <Button onClick={onClose}>Close</Button>
//       </DialogActions>
//     </Dialog>
//   );
// }